import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Offcanvas } from 'react-bootstrap';
import {
  LayoutDashboard, Users, BookOpen, Layers, MessageSquare, UserCog,
  CreditCard, Mic, Video, Brain, Target, HelpCircle, ClipboardList,
  Shield, Settings, LogOut, Calendar, Code, FileText, Moon, Sun,
  Building2, ScrollText, Clock, Zap, PlayCircle, Award, LifeBuoy,
  MessageCircle, Monitor, ClipboardCheck, BarChart3, Database,
  Lock, Wallet, GraduationCap, Trash2, Package, Sparkles, Globe, Percent,
  ShieldCheck, Briefcase, KeyRound, Store, Tag, Megaphone, Link2, PenSquare,
  TrendingUp, Users2, Image as ImageIcon, Radio, BriefcaseBusiness, Flag, Mail,
  Handshake, Receipt, Network, UserRound,
} from 'lucide-react';
import { useSocket } from '../contexts/SocketContext';
import { resolveBrandLogo, resolveBrandName } from '../constants/brand';
import lmsSimpleLogo from '../Assets/Images/Logos/favicon.png';
import { getCurrentUser, logout as authLogout } from '../utils/auth';
import { hasPermission, hasAnyPermission } from '../utils/permissions';
import apiClient from '../utils/apiClient';
import { useBranding } from '../contexts/BrandingContext';
import useBrandMark from '../hooks/useBrandMark';
import { usePlan } from '../contexts/PlanContext';
import { useTheme } from '../contexts/ThemeContext';
import useAudienceTerm from '../hooks/useAudienceTerm';

// Build-time edition flag. Referencing process.env directly (inlined by CRA's
// DefinePlugin) lets the minifier dead-code-eliminate the tenantadmin-only
// branches below in enterprise builds — removing the /sa nav labels/paths.
const IS_ENTERPRISE_BUILD = process.env.REACT_APP_EDITION === 'enterprise';

const simpleLogo = resolveBrandLogo(lmsSimpleLogo);

/**
 * Menu group definitions per user role.
 * Each group: { label?: string, items: [...] }
 * Items: { label, icon, route, badge?, badgeVariant?, beta?, permission? }
 *
 * For admin users, the `permission` key controls visibility.
 * superadmin users see everything (hasPermission always returns true).
 */
// Shared "Help & Guides" group appended at the end of every role's menu.
// Opens the static help site at /docs/ in a new tab (external — not an SPA route).
const HELP_GROUP = {
  label: 'Help',
  items: [
    { label: 'Help & Guides', icon: HelpCircle, route: '/docs/index.html', external: true },
  ],
};

const getMenuGroups = (userLoggedIn, studentSelfScheduling, hasPlanFeature, isFeatureHidden, audienceTerm, distributor = null, hasStudentModule = () => false) => {
  if (!userLoggedIn) return [];
  // `studentModule` on an item: shown only while that Settings → Modules switch
  // is on. Fails closed, unlike `planFeature`.
  const moduleOn = (item) => !item.studentModule || hasStudentModule(item.studentModule);

  // Enterprise on-prem builds have no platform super-admin; gating with the
  // build-time constant lets the minifier drop this whole block (and its
  // /sa/* nav labels + paths) from the bundle.
  if (!IS_ENTERPRISE_BUILD && userLoggedIn.usertype === "tenantadmin") {
    return [
      {
        items: [
          { label: 'Dashboard', icon: LayoutDashboard, route: '/sa' },
          { label: 'Institutes', icon: Building2, route: '/sa/institutes' },
          { label: 'Distributors', icon: Handshake, route: '/sa/distributors' },
          { label: 'Distributor Network', icon: Network, route: '/sa/distributors/network' },
          { label: 'Plans', icon: CreditCard, route: '/sa/plans' },
          { label: 'Licenses', icon: KeyRound, route: '/sa/licenses' },
          { label: 'Zoom Sessions', icon: Monitor, route: '/sa/zoom-hosts' },
          { label: 'Zoho Meeting', icon: Video, route: '/sa/zoho-meeting' },
          { label: 'Grats Meet', icon: Video, route: '/sa/grats-meet' },
          { label: 'Users', icon: Users, route: '/sa/users' },
          { label: 'Audit Logs', icon: ScrollText, route: '/sa/audit-logs' },
          { label: 'Payouts', icon: Wallet, route: '/sa/payouts' },
          { label: 'Revenue share', icon: Percent, route: '/sa/revenue-share' },
          { label: 'Analytics', icon: BarChart3, route: '/sa/analytics' },
          { label: 'Form Templates', icon: FileText, route: '/sa/form-templates' },
          { label: 'Sales Leads', icon: MessageSquare, route: '/sa/sales-leads' },
          { label: 'Service Invoices', icon: FileText, route: '/sa/service-invoices' },
          { label: 'Support', icon: LifeBuoy, route: '/sa/support' },
          { label: 'Storage', icon: Database, route: '/sa/storage' },
          { label: "What's New", icon: Sparkles, route: '/sa/whats-new' },
        ],
      },
      {
        label: 'Marketplace',
        items: [
          { label: 'Quiz Questions', icon: HelpCircle, route: '/sa/marketplace/quiz-questions' },
          { label: 'Assessments', icon: ClipboardList, route: '/sa/marketplace/assessments' },
          { label: 'Coding Questions', icon: Code, route: '/sa/coding-questions' },
        ],
      },
      {
        label: 'Administration',
        items: [
          { label: 'Settings', icon: Settings, route: '/sa/settings' },
          { label: 'WhatsApp Config', icon: MessageCircle, route: '/sa/whatsapp' },
          { label: 'WhatsApp Inbox', icon: MessageCircle, route: '/sa/whatsapp-inbox' },
          { label: 'Zoho CRM Config', icon: Zap, route: '/sa/zoho' },
          { label: 'Account', icon: UserCog, route: '/sa/account-settings' },
        ],
      },
      HELP_GROUP,
    ];
  }

  if (userLoggedIn.usertype === "superadmin" || userLoggedIn.usertype === "admin") {
    // Build full menu, then filter by permission
    const adminMenu = [
      {
        items: [
          { label: 'Dashboard', icon: LayoutDashboard, route: '/dashboard', permission: 'view_dashboards' },
          { label: 'Enquiries', icon: MessageSquare, route: '/enquiries', permission: 'view_enquiries', planFeature: 'enquiryManagement' },
          { label: audienceTerm?.plural || 'Students', icon: Users, route: '/students', permission: 'view_students' },
          // Reads the same student records, so it takes the same permission —
          // a portfolio is a view of a student, not a separate thing to grant.
          { label: 'Portfolios', icon: BriefcaseBusiness, route: '/portfolios', permission: 'view_students' },
          { label: 'Courses', icon: BookOpen, route: '/courses', permission: 'view_courses' },
          { label: 'Batches', icon: Layers, route: '/batches', permission: 'manage_batches' },
          { label: 'Demo Classes', icon: PlayCircle, route: '/demo-classes', planFeature: 'demoClasses' },
          // Same module as Demo Classes, sold separately — an institute may
          // have either flag, both, or neither.
          { label: 'Webinars', icon: Radio, route: '/webinars', planFeature: 'webinars' },
          { label: 'Instructors', icon: UserCog, route: '/instructors', permission: 'view_instructors' },
          { label: 'Attendance', icon: ClipboardCheck, route: '/attendance', permission: 'manage_batches' },
          { label: 'Fees', icon: CreditCard, route: '/fees', permission: 'view_fees', planFeature: 'feeManagement' },
          { label: 'Earnings', icon: Wallet, route: '/earnings', permission: 'view_fees', planFeature: 'feeManagement' },
          { label: 'Self-Paced', icon: PlayCircle, route: '/self-paced', permission: 'view_courses', planFeature: 'selfPacedCourses' },
          
          { label: 'Batch Chat', icon: MessageCircle, route: '/chat' },
          { label: 'Chat Reports', icon: Flag, route: '/chat/reports' },
          { label: 'Feedback', icon: MessageSquare, route: '/feedback' },
          { label: 'Enrollment Requests', icon: GraduationCap, route: '/enrollment-requests', permission: 'view_students' },
        ],
      },
      // Only for an institute the SaaS admin has made a distributor —
      // plan-features carries `distributor` for it and for nobody else, so every
      // other institute's menu is exactly what it was.
      ...(distributor ? [{
        label: 'Distributor',
        items: [
          { label: 'Overview', icon: Handshake, route: '/distributor', permissionFn: () => hasAnyPermission('distributor_view', 'distributor_manage') },
          { label: 'Clients', icon: Building2, route: '/distributor/clients', permissionFn: () => hasAnyPermission('distributor_view', 'distributor_manage') },
          { label: 'Client Billing', icon: Receipt, route: '/distributor/billing', permission: 'distributor_billing' },
        ],
      }] : []),
      {
        label: 'Toolkit',
        items: [
          { label: 'Enquiry Forms', icon: FileText, route: '/enquiry-forms', permission: 'view_enquiries', planFeature: 'enquiryFormBuilder' },
          { label: 'Email Templates', icon: Mail, route: '/email-templates', permissionFn: () => hasAnyPermission('settings_branding', 'settings_notifications') },
          { label: 'Certificate Design', icon: Award, route: '/self-paced/certificate-settings', permission: 'view_courses', planFeature: 'selfPacedCourses' },
          { label: 'Issue Certificates', icon: Award, route: '/self-paced/certificates/issue', permission: 'add_edit_students', planFeature: 'selfPacedCourses' },
        ],
      },
      {
        label: 'Website',
        items: [
          { label: 'CMS Management', icon: Globe, route: '/cms', permission: 'settings_general', planFeature: 'cmsWebsite' },
        ],
      },
      {
        /*
          Sits with the outward-facing tools rather than the academic ones — an
          admissions campaign belongs next to the website, not next to quizzes.

          Ordered by how often they are used, not by the pipeline: an owner
          opens Campaigns and Performance weekly, Ad Accounts once.
        */
        label: 'Marketing',
        items: [
          { label: 'Campaigns', icon: Megaphone, route: '/marketing', permission: 'view_campaigns', planFeature: 'adsManager' },
          { label: 'Create Post', icon: PenSquare, route: '/marketing/compose/new', permission: 'manage_campaigns', planFeature: 'adsManager' },
          { label: 'Performance', icon: TrendingUp, route: '/marketing/performance', permission: 'view_campaigns', planFeature: 'adsManager' },
          { label: 'Images', icon: ImageIcon, route: '/marketing/creatives', permission: 'manage_campaigns', planFeature: 'adsManager' },
          { label: 'Audiences', icon: Users2, route: '/marketing/audiences', permission: 'manage_campaigns', planFeature: 'adsManager' },
          { label: 'Ad Accounts', icon: Link2, route: '/marketing/accounts', permission: 'manage_campaigns', planFeature: 'adsManager' },
        ],
      },
      {
        label: 'Assessments',
        items: [
          { label: 'AI Categories', icon: Tag, route: '/ai-categories', permission: 'view_ai_attempts', planFeatureAny: ['aiQuestionBank', 'aiLearning', 'aiMock'] },
          { label: 'Mock Interview History', icon: Mic, route: '/mock-interview/history', permission: 'view_ai_attempts', planFeature: 'aiMock' },
          { label: 'AIvo – Doubt Settings', icon: Sparkles, route: '/ai-doubt-settings', permission: 'view_ai_attempts', planFeature: 'aiDoubtSolving' },
          { label: 'Interviews', icon: Video, route: '/interviews', permission: 'manage_interviews', planFeature: 'interviews' },
          { label: 'AI Questions', icon: Brain, route: '/ai-questions', permission: 'view_ai_attempts', planFeature: 'aiQuestionBank' },
          { label: 'AI Attempts', icon: Target, route: '/ai-attempts', permission: 'view_ai_attempts', planFeature: 'aiLearning' },
          { label: 'Coding Questions', icon: HelpCircle, route: '/questions-management', permission: 'manage_assignments', planFeature: 'codingPractice' },
          { label: 'Coding Submissions', icon: Code, route: '/coding-submissions', permission: 'manage_assignments', planFeature: 'codingPractice' },
          { label: 'Coding Assignments', icon: ClipboardList, route: '/assignments', permission: 'manage_assignments', planFeature: 'codingPractice' },
          { label: 'Batch Assignments', icon: FileText, route: '/batch-assignments', permission: 'manage_assignments' },
          { label: 'Quiz Center', icon: ClipboardCheck, route: '/quiz-center', permission: 'manage_assignments', planFeature: 'quizAssessment' },
          { label: 'Test Series', icon: Layers, route: '/test-series', permission: 'manage_assignments', planFeature: 'quizAssessment' },
          { label: 'Public Exams', icon: ShieldCheck, route: '/public-exams', permission: 'manage_assignments', planFeature: 'publicExams' },
        ],
      },
      {
        label: 'Marketplace',
        items: [
          { label: 'Home', icon: Store, route: '/marketplace' },
          { label: 'Quiz Banks', icon: HelpCircle, route: '/marketplace/quiz-banks', permission: 'manage_assignments', planFeature: 'quizAssessment' },
          { label: 'Coding Questions', icon: Code, route: '/marketplace/coding', permission: 'manage_assignments', planFeature: 'codingPractice' },
        ],
      },
      {
        label: 'Compliance',
        items: [
          { label: 'Mandatory Training', icon: ShieldCheck, route: '/compliance/mandatory-training', permission: 'manage_mandatory_training', planFeature: 'cbtEnterprise' },
          { label: 'Employee Groups', icon: Briefcase, route: '/compliance/employee-groups', permission: 'manage_employee_groups', planFeature: 'cbtEnterprise' },
        ],
      },
      {
        label: 'Administration',
        items: [
          ...(userLoggedIn.usertype === "superadmin"
            ? [{ label: 'Admins', icon: Shield, route: '/admins', permission: 'manage_admins' }]
            : []),
          { label: 'Branches', icon: Building2, route: '/branches', permission: 'manage_branches', planFeature: 'multiBranch' },
          { label: 'Assets', icon: Package, route: '/assets', permission: 'manage_assets', planFeature: 'assetsManagement' },
          { label: 'Institute Calendar', icon: Calendar, route: '/institute/calendar' },
          { label: 'Billing & Plan', icon: CreditCard, route: '/billing' },
          { label: 'Service Invoices', icon: FileText, route: '/service-invoices' },
          { label: 'Storage', icon: Database, route: '/storage' },
          { label: 'Trash', icon: Trash2, route: '/trash' },
          { label: 'WhatsApp', icon: MessageCircle, route: '/whatsapp', planFeature: 'whatsapp' },
          // Calls is deliberately NOT here — it lives as a tab under Enquiries.
          // An enquiry is the reason for a call and a call is an event on an
          // enquiry, so a counsellor works both at once; two sidebar entries made
          // them cross the whole nav on every switch.
          { label: 'Support', icon: LifeBuoy, route: '/support/tickets' },
          { label: 'Settings', icon: Settings, route: '/settings', permissionFn: () => hasAnyPermission('settings_general', 'settings_notifications', 'settings_branding', 'settings_payments', 'settings_zoom') },
          { label: 'Account', icon: UserCog, route: '/account-settings' },
        ],
      },
      HELP_GROUP,
    ];

    // Filter items by permission and plan access — hide items not in plan
    return adminMenu
      .map(group => ({
        ...group,
        items: group.items
          .filter(item => {
            if (item.permissionFn && !item.permissionFn()) return false;
            if (item.permission && !hasPermission(item.permission)) return false;
            if (item.planFeature && isFeatureHidden(item.planFeature)) return false;
            if (item.planFeature && !hasPlanFeature(item.planFeature)) return false;
            // planFeatureAny: visible if at least one listed feature is enabled & not hidden
            if (item.planFeatureAny && !item.planFeatureAny.some(f => !isFeatureHidden(f) && hasPlanFeature(f))) return false;
            if (!moduleOn(item)) return false;
            return true;
          }),
      }))
      .filter(group => group.items.length > 0);
  }

  if (userLoggedIn.usertype === "instructor") {
    const instructorMenu = [
      {
        items: [
          { label: 'Profile', icon: LayoutDashboard, route: '/i/' },
          // The server narrows this to students in the instructor's own
          // batches (vc_be/utils/portfolioAccess), so no permission gate here
          // would add anything the list does not already enforce.
          { label: 'Portfolios', icon: BriefcaseBusiness, route: '/i/portfolios' },
          { label: 'Attendance', icon: ClipboardCheck, route: '/i/attendance', planFeature: 'offlineAttendance' },
          { label: 'Batch Chat', icon: MessageCircle, route: '/i/chat' },
          { label: 'Account', icon: Settings, route: '/i/account-settings' },
        ],
      },
      {
        label: 'Self-Paced',
        items: [
          { label: 'Teaching Library', icon: PlayCircle, route: '/i/self-paced', planFeature: 'selfPacedCourses' },
        ],
      },
      {
        label: 'Assessments',
        items: [
          { label: 'Questions Management', icon: HelpCircle, route: '/i/questions-management', planFeature: 'codingPractice' },
          { label: 'Coding Submissions', icon: Code, route: '/i/coding-submissions', planFeature: 'codingPractice' },
          { label: 'Coding Assignments', icon: ClipboardList, route: '/i/assignments', planFeature: 'codingPractice' },
          { label: 'Batch Assignments', icon: FileText, route: '/i/batch-assignments' },
        ],
      },
      {
        label: 'Schedule',
        items: [
          { label: 'Calendar', icon: Calendar, route: '/i/calendar' },
          { label: 'Support', icon: LifeBuoy, route: '/i/support' },
        ],
      },
      HELP_GROUP,
    ];

    return instructorMenu
      .map(group => ({
        ...group,
        items: group.items
          .filter(item => {
            if (item.planFeature && isFeatureHidden(item.planFeature)) return false;
            if (item.planFeature && !hasPlanFeature(item.planFeature)) return false;
            return true;
          }),
      }))
      .filter(group => group.items.length > 0);
  }

  if (userLoggedIn.usertype === "student") {
    const studentMenu = [
      {
        items: [
          { label: 'Dashboard', icon: LayoutDashboard, route: '/s/dashboard' },
          { label: 'Batch Chat', icon: MessageCircle, route: '/s/chat' },
          { label: 'My Learning', icon: BookOpen, route: '/s/learning' },
          { label: 'My Fees', icon: Wallet, route: '/s/fees', planFeature: 'feeManagement' },
          { label: 'My Profile', icon: UserRound, route: '/s/profile', studentModule: 'advancedProfile' },
        ],
      },
      {
        label: 'Live Courses',
        items: [
          { label: 'Browse Live Courses', icon: GraduationCap, route: '/s/courses' },
          { label: 'My Applications', icon: ClipboardList, route: '/s/courses/my-requests' },
        ],
      },
      {
        label: 'Self-Paced',
        items: [
          { label: 'Browse Courses', icon: PlayCircle, route: '/s/self-paced', planFeature: 'selfPacedCourses' },
          { label: 'My Enrollments', icon: BookOpen, route: '/s/self-paced/enrollments', planFeature: 'selfPacedCourses' },
          { label: 'Certificates', icon: Award, route: '/s/self-paced/certificates', planFeature: 'selfPacedCourses' },
        ],
      },
      {
        label: 'Compliance',
        items: [
          { label: 'My Mandatory Training', icon: ShieldCheck, route: '/s/compliance', planFeature: 'cbtEnterprise' },
        ],
      },
      {
        label: 'AI & Practice',
        items: [
          { label: 'Mock With AI', icon: Mic, route: '/s/mock-interview/setup', beta: true, planFeature: 'aiMock' },
          { label: 'Mock Interview History', icon: FileText, route: '/s/mock-interview/history', planFeature: 'aiMock' },
          { label: 'Learn with AI', icon: Brain, route: '/s/learn-with-ai', planFeature: 'aiLearning' },
          { label: 'AI History', icon: FileText, route: '/s/learn-ai/history', planFeature: 'aiLearning' },
          { label: 'Coding Practice', icon: Code, route: '/s/practice', planFeature: 'codingPractice' },
        ],
      },
      {
        label: 'Assessments',
        items: [
          { label: 'Coding Assignments', icon: ClipboardList, route: '/s/assignments', planFeature: 'codingPractice' },
          { label: 'My Interviews', icon: Video, route: '/s/interviews', planFeature: 'interviews' },
          { label: 'Quizzes & Tests', icon: ClipboardCheck, route: '/s/assessments', planFeature: 'quizAssessment' },
          { label: 'Test Series', icon: Layers, route: '/s/test-series', planFeature: 'quizAssessment' },
        ],
      },
      {
        label: 'Schedule',
        items: [
          { label: 'My Attendance', icon: ClipboardCheck, route: '/s/attendance', planFeature: 'attendanceManagement' },
          { label: 'Feedback', icon: MessageSquare, route: '/s/feedback' },
          { label: 'Calendar', icon: Calendar, route: '/s/calendar' },
          { label: 'Support', icon: LifeBuoy, route: '/s/support' },
          { label: 'Account', icon: Settings, route: '/s/account-settings' },
        ],
      },
    ];

    return studentMenu
      .map(group => ({
        ...group,
        items: group.items
          .filter(item => {
            if (item.planFeature && isFeatureHidden(item.planFeature)) return false;
            if (item.planFeature && !hasPlanFeature(item.planFeature)) return false;
            if (!moduleOn(item)) return false;
            return true;
          }),
      }))
      .filter(group => group.items.length > 0);
  }

  return [];
};

/**
 * Single menu group with optional label and separator
 */
const MenuGroup = ({ group, onNavItemClick, isLast }) => (
  <div className="sidebar-group">
    {group.label && (
      <div className="sidebar-group-label">{group.label}</div>
    )}
    <div className="sidebar-group-content">
      {group.items.map((item) => {
        if (!item) return null;
        const Icon = item.icon;
        // External links (e.g. the static /docs/ help site) open in a new tab
        // and must not go through the SPA router.
        if (item.external) {
          return (
            <a
              href={item.route}
              key={item.label}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onNavItemClick}
              className="sidebar-menu-item"
            >
              <span className="sidebar-icon">
                <Icon size={16} strokeWidth={1.8} />
              </span>
              <span className="sidebar-label">{item.label}</span>
            </a>
          );
        }
        return (
          <NavLink
            to={item.route}
            key={item.label}
            end
            onClick={onNavItemClick}
            className={({ isActive }) =>
              `sidebar-menu-item${isActive && !item.locked ? ' active' : ''}${item.locked ? ' sidebar-menu-item--locked' : ''}`
            }
          >
            <span className="sidebar-icon">
              <Icon size={16} strokeWidth={1.8} />
            </span>
            <span className="sidebar-label">{item.label}</span>
            {item.locked && (
              <span className="sidebar-lock-icon">
                <Lock size={14} />
              </span>
            )}
            {!item.locked && item.badge && (
              <span className={`sidebar-badge${item.badgeVariant === 'highlight' ? ' sidebar-badge--highlight' : ''}`}>
                {item.badge}
              </span>
            )}
            {!item.locked && item.beta && (
              <span className="sidebar-badge sidebar-badge--beta">Beta</span>
            )}
          </NavLink>
        );
      })}
    </div>
    {!isLast && <div className="sidebar-separator" />}
  </div>
);

/**
 * Compact trial banner for the sidebar — only shows for trial-status institutes.
 */
const SidebarTrialBanner = ({ trial, onClick }) => {
  if (!trial?.active) return null;
  const isUrgent = trial.daysLeft <= 3;
  return (
    <div
      className={`sidebar-trial-banner ${isUrgent ? 'sidebar-trial-banner--urgent' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <div className="sidebar-trial-banner__icon">
        {isUrgent ? <Clock size={16} /> : <Clock size={16} />}
      </div>
      <div className="sidebar-trial-banner__text">
        {trial.daysLeft === 0 ? (
          <span>Trial expires <strong>today</strong></span>
        ) : (
          <span><strong>{trial.daysLeft}d</strong> left in trial</span>
        )}
      </div>
      <div className="sidebar-trial-banner__cta">
        <Zap size={12} /> Upgrade
      </div>
    </div>
  );
};

// Sidebar component
const Sidebar = ({ show, onHide, isOffcanvas }) => {
  const navigate = useNavigate();
  const userLoggedIn = getCurrentUser();
  const branding = useBranding();
  const brandMark = useBrandMark();
  const { hasPlanFeature, isFeatureHidden, trial, distributor, hasStudentModule } = usePlan();
  const audienceTerm = useAudienceTerm();
  const { unreadChatCount, unreadWhatsAppCount } = useSocket();
  const { theme, toggleTheme } = useTheme();
  const [studentSelfScheduling, setStudentSelfScheduling] = useState(false);
  const [pendingPayoutsCount, setPendingPayoutsCount] = useState(0);

  useEffect(() => {
    if (userLoggedIn?.usertype !== 'student') return;
    apiClient.get('/institute/config')
      .then(res => setStudentSelfScheduling(!!res.data?.config?.studentSelfScheduling))
      .catch(() => {});
  }, [userLoggedIn?.usertype]);

  useEffect(() => {
    if (IS_ENTERPRISE_BUILD) return; // no platform payouts on-prem (DCE'd)
    if (userLoggedIn?.usertype !== 'tenantadmin') return;
    apiClient.get('/sa/payouts/pending-count')
      .then(res => setPendingPayoutsCount(res.data?.count || 0))
      .catch(() => {});
  }, [userLoggedIn?.usertype]);

  const menuGroups = React.useMemo(
    () => {
      const groups = getMenuGroups(userLoggedIn, studentSelfScheduling, hasPlanFeature, isFeatureHidden, audienceTerm, distributor, hasStudentModule);
      // Inject unread chat badge on "Batch Chat" items
      if (unreadChatCount > 0) {
        for (const group of groups) {
          for (const item of group.items) {
            if (item.label === 'Batch Chat') {
              item.badge = unreadChatCount > 99 ? '99+' : String(unreadChatCount);
              item.badgeVariant = 'highlight';
            }
          }
        }
      }
      // Inject pending payouts count badge on "Payouts" item
      if (pendingPayoutsCount > 0) {
        for (const group of groups) {
          for (const item of group.items) {
            if (item.label === 'Payouts') {
              item.badge = String(pendingPayoutsCount);
              item.badgeVariant = 'highlight';
            }
          }
        }
      }
      // Inject unread WhatsApp badge on WhatsApp menu items
      if (unreadWhatsAppCount > 0) {
        const waBadge = unreadWhatsAppCount > 99 ? '99+' : String(unreadWhatsAppCount);
        for (const group of groups) {
          for (const item of group.items) {
            if (item.label === 'WhatsApp' || item.label === 'WhatsApp Inbox') {
              item.badge = waBadge;
              item.badgeVariant = 'highlight';
            }
          }
        }
      }
      return groups;
    },
    [userLoggedIn, studentSelfScheduling, hasPlanFeature, isFeatureHidden, audienceTerm, distributor, hasStudentModule, unreadChatCount, pendingPayoutsCount, unreadWhatsAppCount]
  );

  const handleLogout = async () => {
    const redirectPath = (!IS_ENTERPRISE_BUILD && userLoggedIn?.usertype === 'tenantadmin') ? '/ta/login' : '/login';
    // Clean up push subscriptions + clear auth data
    await authLogout();
    sessionStorage.clear();
    navigate(redirectPath);
  };

  const userInitials = userLoggedIn?.name
    ? userLoggedIn.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'SA';

  // Branding: tenantadmin sees platform branding; institute users see merged
  // branding, and never ours — an institute without a logo shows its initial,
  // and one whose branding is still loading shows an empty mark (useBrandMark).
  const isTenantAdmin = userLoggedIn?.usertype === 'tenantadmin';
  const brandName = isTenantAdmin ? (branding.brandName || resolveBrandName('LMS Campus')) : brandMark.name;
  const brandSub = isTenantAdmin ? (branding.brandSubtitle || 'Platform Admin') : branding.brandSubtitle;
  const brandLogo = isTenantAdmin ? (branding.faviconUrl || branding.logoUrl || simpleLogo) : brandMark.icon;
  const logoBgColor = branding.logoBgColor || '#3b82f6';
  const brandLogoEl = brandLogo
    ? <img src={brandLogo} alt="Logo" className="sidebar-favicon" style={{ background: logoBgColor }} />
    : (
      <span className={`sidebar-favicon sidebar-favicon--mark${brandMark.pending ? ' is-pending' : ''}`} aria-hidden="true">
        {brandMark.pending ? '' : brandMark.initial}
      </span>
    );

  // Shared sidebar content (grouped menu + user footer)
  const sidebarContent = (navItemClick) => (
    <>
      <div className="sidebar-navigation-wrapper">
        {menuGroups.map((group, idx) => (
          <MenuGroup
            key={group.label || idx}
            group={group}
            onNavItemClick={navItemClick}
            isLast={idx === menuGroups.length - 1}
          />
        ))}
      </div>
      {userLoggedIn?.usertype !== 'student' && userLoggedIn?.usertype !== 'instructor' && (
        <SidebarTrialBanner trial={trial} onClick={() => navigate('/billing')} />
      )}
      <div className="sidebar-footer">
        <div className="sidebar-user-section">
          <div className="sidebar-user-avatar">{userInitials}</div>
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{userLoggedIn?.name || 'User'}</span>
            <span className="sidebar-user-role">{userLoggedIn?.usertype || 'User'}</span>
          </div>
          <button className="sidebar-theme-btn" onClick={toggleTheme} title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}>
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
        </div>
        <button className="sidebar-logout-btn" onClick={handleLogout}>
          <LogOut size={16} />
          <span>Logout</span>
        </button>
      </div>
    </>
  );

  // Offcanvas (mobile) sidebar
  if (isOffcanvas) {
    return (
      <Offcanvas
        show={show}
        onHide={onHide}
        placement="start"
        className="dashboard-sidebar d-md-none"
      >
        <Offcanvas.Header closeButton className="sidebar-header">
          <Offcanvas.Title>
            <div className="sidebar-logo-wrapper">
              {brandLogoEl}
              <div className="sidebar-title">
                <div className="sidebar-title-main">{brandName}</div>
                <div className="sidebar-title-sub">{brandSub}</div>
              </div>
            </div>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="sidebar-body-mobile">
          {sidebarContent(onHide)}
        </Offcanvas.Body>
      </Offcanvas>
    );
  }

  // Static (desktop) sidebar
  return (
    <div className="dashboard-sidebar d-none d-md-flex flex-column">
      <div className="sidebar-header">
        <div className="sidebar-logo-wrapper">
          {brandLogoEl}
          <div className="sidebar-title">
            <div className="sidebar-title-main">{brandName}</div>
            <div className="sidebar-title-sub">{brandSub}</div>
          </div>
        </div>
      </div>
      {sidebarContent(undefined)}
    </div>
  );
};

export default Sidebar;
